/**
 * Project / Run Context
 *
 * Persistent selection of the active project and run, retained as the user
 * navigates between pages. Backs the GlobalContextBar selectors so a chosen
 * project/run stays put across the app. Mirrors edna's HierarchyContext pattern.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

const STORAGE_PROJECT = 'mediasim.selectedProjectId';
const STORAGE_RUN = 'mediasim.selectedRunId';

interface ProjectRunContextValue {
  selectedProjectId: string | null;
  selectedRunId: string | null;
  selectProject: (projectId: string | null) => void;
  selectRun: (runId: string | null) => void;
}

const ProjectRunContext = createContext<ProjectRunContextValue | undefined>(undefined);

function readStored(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

export function ProjectRunProvider({ children }: { children: ReactNode }) {
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(() =>
    readStored(STORAGE_PROJECT)
  );
  const [selectedRunId, setSelectedRunId] = useState<string | null>(() =>
    readStored(STORAGE_RUN)
  );

  useEffect(() => {
    try {
      if (selectedProjectId) window.localStorage.setItem(STORAGE_PROJECT, selectedProjectId);
      else window.localStorage.removeItem(STORAGE_PROJECT);
    } catch { /* ignore storage errors */ }
  }, [selectedProjectId]);

  useEffect(() => {
    try {
      if (selectedRunId) window.localStorage.setItem(STORAGE_RUN, selectedRunId);
      else window.localStorage.removeItem(STORAGE_RUN);
    } catch { /* ignore storage errors */ }
  }, [selectedRunId]);

  // Changing project clears the run selection (a run belongs to one project).
  const selectProject = useCallback((projectId: string | null) => {
    setSelectedProjectId(projectId);
    setSelectedRunId(null);
  }, []);

  const selectRun = useCallback((runId: string | null) => {
    setSelectedRunId(runId);
  }, []);

  const value = useMemo(
    () => ({ selectedProjectId, selectedRunId, selectProject, selectRun }),
    [selectedProjectId, selectedRunId, selectProject, selectRun]
  );

  return <ProjectRunContext.Provider value={value}>{children}</ProjectRunContext.Provider>;
}

export function useProjectRun(): ProjectRunContextValue {
  const ctx = useContext(ProjectRunContext);
  if (!ctx) {
    throw new Error('useProjectRun must be used within a ProjectRunProvider');
  }
  return ctx;
}
